import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  BookOpen,
  Building2,
  Sparkles,
  RotateCcw,
  Loader2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
const decideBg = { url: "/assets/decide-bg.png" };
import { useSfx } from "@/hooks/useSfx";
import type { BookInsight } from "./BookInsightCard";
import type { LessonCompanyCase } from "./CompanyCaseBlock";
import { isLaunchLessonId } from "@/lib/launch/launchExperienceRoster";
import { useAccessTier } from "@/hooks/useAccessTier";
import { LumiCoachingCard } from "@/components/lumi/LumiCoachingCard";
import {
  getLumiDecideMissCoaching,
  getLumiDecidePassCoaching,
} from "@/data/lumiLaunchCoaching";
import { useTriggerLessonVoiceMoment } from "@/hooks/useLessonVoiceMoment";
import { getLessonMomentFor } from "@/lib/audio/lessonVoiceMap";
import { getCuratedDecideCheck } from "@/data/launchDecideQuestions";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export type DecideSourceType =
  | "book_insight"
  | "company_case"
  | "combined_application";

export interface DecideQuestion {
  question_id: string;
  source_type: DecideSourceType;
  related_insight_section: string;
  question_text: string;
  answer_choices: string[];
  correct_index: number;
  correct_explanation: string;
  incorrect_explanations: Record<string, string>;
}

interface DecideCheck {
  questions: DecideQuestion[];
  minimum_pass_score: number;
}

interface Props {
  lessonId: string;
  lessonName: string;
  decisionPrompt?: string | null;
  masteryPrinciple?: string | null;
  bookInsights?: BookInsight[];
  companyCases?: LessonCompanyCase[];
  onPass: () => void;
  onReviewInsight: () => void;
}

// ─────────────────────────────────────────────────────────────
// Built-in canonical fallback for "Needs vs Wants" lesson.
// Used if the AI generator fails. Matches the example in spec.
// ─────────────────────────────────────────────────────────────

const NEEDS_VS_WANTS_FALLBACK: DecideCheck = {
  minimum_pass_score: 2,
  questions: [
    {
      question_id: "q1",
      source_type: "book_insight",
      related_insight_section: "Book Insight: Your Money or Your Life",
      question_text:
        "According to the Book Insight, what is the real cost of a $5 daily coffee habit?",
      answer_choices: [
        "It is not just $5 — it represents the life energy and work hours traded to earn that money.",
        "It only matters if the coffee causes debt.",
        "It is automatically a bad purchase because all wants are wrong.",
        "It only matters if the coffee is more expensive than other drinks.",
      ],
      correct_index: 0,
      correct_explanation:
        "Your Money or Your Life frames every dollar as life energy — hours of life traded for money. The true cost of the coffee is the time you spent earning it, not the price tag.",
      incorrect_explanations: {
        "1": "Debt is one consequence, but the Book Insight says every dollar carries a life-energy cost regardless of whether it causes debt.",
        "2": "The Insight does not call wants 'wrong' — it asks you to weigh them against the life energy they cost.",
        "3": "Comparing prices misses the point. The cost is your time and life energy, not the price relative to other drinks.",
      },
    },
    {
      question_id: "q2",
      source_type: "company_case",
      related_insight_section: "Company Case: Starbucks",
      question_text: "What did the Starbucks case show about needs and wants?",
      answer_choices: [
        "Coffee is always a financial need.",
        "Companies can make a functional want feel like an emotional need through branding, atmosphere, and habit.",
        "Premium products are always scams.",
        "A product becomes a need when many people buy it.",
      ],
      correct_index: 1,
      correct_explanation:
        "Starbucks turned a $0.50 commodity into a $6 experience by making customers emotionally perceive it as more than a functional want — that is the exact mechanism the case proves.",
      incorrect_explanations: {
        "0": "Coffee is functionally a want. The case shows how branding makes it feel like a need.",
        "2": "The case is about perception engineering, not fraud. Premium pricing is not the same as a scam.",
        "3": "Popularity does not change a want into a need. The case shows perception, not headcount, drives the shift.",
      },
    },
    {
      question_id: "q3",
      source_type: "combined_application",
      related_insight_section: "Applied: Book Insight + Starbucks Case",
      question_text:
        "Based on the Book Insight and Starbucks case, what is the strongest financial decision?",
      answer_choices: [
        "Keep buying it because small purchases never affect long-term wealth.",
        "Cut out all enjoyable spending because wants have no value.",
        "Recognize the coffee as a want, decide whether the life-energy tradeoff is worth it, and consider redirecting some spending toward a future goal.",
        "Only compare the coffee price to other coffee shops.",
      ],
      correct_index: 2,
      correct_explanation:
        "This combines both ideas: the Book Insight (life-energy tradeoff) and the Company Case (branding turns wants into perceived needs). The strongest decision is conscious — weigh the tradeoff, then redirect some spending toward future wealth.",
      incorrect_explanations: {
        "0": "$5/day is $1,825/year — half invested at 8% for 20 years is over $20,000. Small purchases compound.",
        "1": "Wants have value. The Book Insight asks you to weigh them, not eliminate them.",
        "3": "Comparison shopping ignores the core idea: the cost is your life energy, not the price relative to competitors.",
      },
    },
  ],
};

// ─────────────────────────────────────────────────────────────
// Shuffle answer choices per question while preserving the
// correct-answer mapping and incorrect-explanation keys. Runs
// once per payload so order is stable for the learner's session.
// ─────────────────────────────────────────────────────────────
export function shuffleDecideCheck(check: DecideCheck): DecideCheck {
  return {
    ...check,
    questions: check.questions.map((q) => {
      const n = q.answer_choices.length;
      // Build permutation [newIndex] -> originalIndex
      const order = Array.from({ length: n }, (_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      const newChoices = order.map((origIdx) => q.answer_choices[origIdx]);
      const newCorrectIndex = order.indexOf(q.correct_index);
      const newIncorrect: Record<string, string> = {};
      order.forEach((origIdx, newIdx) => {
        if (newIdx === newCorrectIndex) return;
        const fromOrig = q.incorrect_explanations?.[String(origIdx)];
        if (fromOrig) newIncorrect[String(newIdx)] = fromOrig;
      });
      return {
        ...q,
        answer_choices: newChoices,
        correct_index: newCorrectIndex,
        incorrect_explanations: newIncorrect,
      };
    }),
  };
}

function firstSentence(value: string | null | undefined, fallback: string): string {
  const text = (value || "").trim();
  if (!text) return fallback;
  const sentence = text.match(/^(.+?[.!?])(?:\s|$)/)?.[1] || text;
  return sentence.length > 190 ? `${sentence.slice(0, 187).trim()}…` : sentence;
}

function buildLocalFallbackCheck({
  lessonName,
  decisionPrompt,
  masteryPrinciple,
  bookInsights = [],
  companyCases = [],
}: Pick<Props, "lessonName" | "decisionPrompt" | "masteryPrinciple" | "bookInsights" | "companyCases">): DecideCheck {
  const insight = bookInsights.find((i) => i.is_primary) || bookInsights[0];
  const companyCase = companyCases[0];
  const principle = firstSentence(
    masteryPrinciple || insight?.mastery_principle,
    `Use the key principle from ${lessonName} to compare choices by long-term results, not just what feels good right now.`,
  );
  const bookIdea = firstSentence(
    insight?.perspective || insight?.why_it_matters || insight?.mastery_principle,
    principle,
  );
  const caseIdea = firstSentence(
    companyCase?.what_this_proves || companyCase?.why_it_matters_here || companyCase?.what_happened,
    `The case shows why ${lessonName} matters in real financial decisions.`,
  );
  const scenario = firstSentence(
    decisionPrompt || companyCase?.decision_prompt,
    `You are making a decision connected to ${lessonName}.`,
  );
  const bookLabel = insight?.book?.title ? `Book Insight: ${insight.book.title}` : "Book Insight";
  const caseLabel = companyCase?.company?.company_name ? `Company Case: ${companyCase.company.company_name}` : "Company Case";

  return {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "local-q1",
        source_type: "book_insight",
        related_insight_section: bookLabel,
        question_text: `What is the strongest takeaway from the Book Insight for ${lessonName}?`,
        answer_choices: [
          bookIdea,
          "Only short-term income matters; long-term assets and tradeoffs can be ignored.",
          "A decision is automatically smart if it is popular or feels normal.",
          "The lesson only applies to adults and not to everyday teen money choices.",
        ],
        correct_index: 0,
        correct_explanation: `Correct — the Book Insight points to this idea: ${bookIdea}`,
        incorrect_explanations: {
          "1": "That ignores the lesson's focus on long-term outcomes and tradeoffs.",
          "2": "Popularity does not prove a choice is financially strong.",
          "3": "The lesson is designed to apply to everyday choices learners can recognize now.",
        },
      },
      {
        question_id: "local-q2",
        source_type: "company_case",
        related_insight_section: caseLabel,
        question_text: `What did the Company Case show about ${lessonName}?`,
        answer_choices: [
          caseIdea,
          "Companies always make the best financial decisions for customers.",
          "A brand or headline matters more than the actual money outcome.",
          "Financial choices do not need evidence if they seem exciting.",
        ],
        correct_index: 0,
        correct_explanation: `Correct — the Company Case proves this idea: ${caseIdea}`,
        incorrect_explanations: {
          "1": "The case is evidence to analyze, not a promise that every company choice helps customers.",
          "2": "The case asks you to look past surface signals and evaluate the actual outcome.",
          "3": "The case matters because it gives evidence for better decision-making.",
        },
      },
      {
        question_id: "local-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Book Insight + Company Case",
        question_text: `Based on both examples, what is the strongest decision in this scenario: ${scenario}`,
        answer_choices: [
          `Apply the principle: ${principle}`,
          "Choose whatever option feels best right now, then think about the money impact later.",
          "Copy what most people do because common choices are always financially safe.",
          "Ignore the case and the insight because real decisions are only about personal preference.",
        ],
        correct_index: 0,
        correct_explanation: `This combines the Book Insight and Company Case: ${principle}`,
        incorrect_explanations: {
          "1": "That delays the financial thinking until after the decision, which is exactly what the lesson warns against.",
          "2": "Common behavior can still create weak financial outcomes.",
          "3": "The decision should use evidence from both learning sections, not ignore it.",
        },
      },
    ],
  };
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function DecisionCheckStage({
  lessonId,
  lessonName,
  decisionPrompt,
  masteryPrinciple,
  bookInsights,
  companyCases,
  onPass,
  onReviewInsight,
}: Props) {
  const cacheKey = `decide-check:${lessonId}`;
  const { playSfx } = useSfx();
  const { tier: accessTier } = useAccessTier();
  const lumiEnabled = accessTier !== "admin" && isLaunchLessonId(lessonId);

  // Lazy initializer: read cached, already-shuffled payload synchronously so
  // the Decide tab opens instantly when prefetch has already completed.
  const readCache = (): DecideCheck | null => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as DecideCheck;
      return parsed?.questions?.length === 3 ? parsed : null;
    } catch {
      return null;
    }
  };

  // Curated questions for beta lessons take precedence over runtime AI.
  // They are deterministic, grounded in the exact case/insight the learner
  // just saw, and meet the premium HeirWise quality bar.
  const curated = getCuratedDecideCheck(lessonId);

  const initialShuffled = (): DecideCheck | null => {
    if (curated) return shuffleDecideCheck(curated);
    return readCache();
  };

  const [check, setCheck] = useState<DecideCheck | null>(() => initialShuffled());
  // Stash the un-shuffled base so retries can reshuffle without refetching.
  const [baseCheck, setBaseCheck] = useState<DecideCheck | null>(() => curated ?? readCache());
  const [loading, setLoading] = useState(() => !curated && readCache() === null);
  const [error, setError] = useState<string | null>(null);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [results, setResults] = useState<boolean[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Beta lessons: use the curated set, skip the AI call entirely.
      if (curated) {
        if (!cancelled) setLoading(false);
        return;
      }
      // If we already hydrated from cache synchronously, skip the network call.
      if (readCache()) {
        if (!cancelled) setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      try {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const timeout = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error("Decision Check timed out")), 6000);
        });
        const { data, error: fnError } = await Promise.race([
          supabase.functions.invoke("generate-decide-questions", { body: { lessonId } }),
          timeout,
        ]);
        if (timeoutId) clearTimeout(timeoutId);
        if (fnError) throw fnError;
        const payload = data as DecideCheck;
        if (!payload?.questions || payload.questions.length !== 3) {
          throw new Error("Malformed payload");
        }
        const shuffled = shuffleDecideCheck(payload);
        if (!cancelled) {
          setCheck(shuffled);
          setBaseCheck(payload);
          sessionStorage.setItem(cacheKey, JSON.stringify(shuffled));
        }
      } catch (e: unknown) {
        if (cancelled) return;
        const fallback = /needs?\s*vs\.?\s*wants?/i.test(lessonName)
          ? NEEDS_VS_WANTS_FALLBACK
          : buildLocalFallbackCheck({ lessonName, decisionPrompt, masteryPrinciple, bookInsights, companyCases });
        const shuffled = shuffleDecideCheck(fallback);
        setCheck(shuffled);
        setBaseCheck(fallback);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(shuffled)); } catch { /* ignore */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  const question = check?.questions[currentIdx];
  const totalQuestions = check?.questions.length ?? 3;
  const minPass = check?.minimum_pass_score ?? 2;
  const isLastQuestion = currentIdx >= totalQuestions - 1;
  const isFinished = results.length === totalQuestions;
  const passed = isFinished && score >= minPass;

  // ───────── Beta lesson voice moments ─────────
  // Aelyra / Caelion deliver a short mentor line before the very first
  // decision and again after it is submitted. Fires at most once per
  // lesson per stage and only for the first question.
  const preDecisionMomentId = useMemo(
    () => getLessonMomentFor(lessonId, "pre_decision")?.momentId ?? null,
    [lessonId],
  );
  const postDecisionMomentId = useMemo(
    () => getLessonMomentFor(lessonId, "post_decision")?.momentId ?? null,
    [lessonId],
  );
  useTriggerLessonVoiceMoment(
    preDecisionMomentId,
    !loading && !!question && currentIdx === 0 && !submitted,
  );
  useTriggerLessonVoiceMoment(
    postDecisionMomentId,
    !loading && !!question && currentIdx === 0 && submitted,
  );

  const sourceMeta = useMemo(() => {
    if (!question) return null;
    switch (question.source_type) {
      case "book_insight":
        return {
          icon: BookOpen,
          label: "Book Insight Check",
          tint: "hsl(265 90% 72%)",
        };
      case "company_case":
        return {
          icon: Building2,
          label: "Company Case Check",
          tint: "hsl(150 75% 60%)",
        };
      case "combined_application":
        return {
          icon: Sparkles,
          label: "Applied Decision Check",
          tint: "hsl(38 95% 65%)",
        };
    }
  }, [question]);

  const handleSelect = (idx: number) => {
    if (submitted) return;
    playSfx("decision_select");
    setSelectedIdx(idx);
  };

  const handleSubmit = () => {
    if (selectedIdx === null || !question) return;
    const correct = selectedIdx === question.correct_index;
    playSfx(correct ? "correct_soft" : "incorrect_soft");
    setSubmitted(true);
    setResults((prev) => [...prev, correct]);
    if (correct) setScore((s) => s + 1);
  };

  const handleNext = () => {
    if (isLastQuestion) return; // results screen handles next
    playSfx("soft_swoosh");
    setCurrentIdx((i) => i + 1);
    setSelectedIdx(null);
    setSubmitted(false);
  };

  const handleRetry = () => {
    // Reshuffle existing payload for a fresh attempt — no refetch, no loading
    // state, and answer positions change so learners can't memorize them.
    const source = baseCheck ?? check;
    if (source) {
      const reshuffled = shuffleDecideCheck(source);
      setCheck(reshuffled);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(reshuffled)); } catch { /* ignore */ }
    }
    setCurrentIdx(0);
    setSelectedIdx(null);
    setSubmitted(false);
    setScore(0);
    setResults([]);
  };

  // ───────── Loading / Error ─────────
  if (loading) {
    return (
      <DecideShell lessonName={lessonName}>
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-foreground/70">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="font-body text-sm">Preparing your Applied Decision Check…</p>
        </div>
      </DecideShell>
    );
  }

  if (error || !check || !question) {
    return (
      <DecideShell lessonName={lessonName}>
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center space-y-4">
          <p className="font-body text-sm text-foreground/85">
            {error || "No Decision Check available for this lesson yet."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="outline" size="sm" onClick={onReviewInsight}>
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Review Insight
            </Button>
            <Button size="sm" onClick={handleRetry}>
              <RotateCcw className="mr-1.5 h-4 w-4" /> Try again
            </Button>
          </div>
        </div>
      </DecideShell>
    );
  }

  // ───────── Results screen ─────────
  if (isFinished) {
    return (
      <DecideShell lessonName={lessonName}>
        <div className="space-y-6">
          <div
            className={`rounded-2xl border p-6 text-center space-y-3 backdrop-blur-md ${
              passed
                ? "border-emerald-400/40 bg-emerald-400/5 shadow-[0_0_30px_-10px_hsl(150_85%_60%/0.5)]"
                : "border-rose-400/40 bg-rose-400/5 shadow-[0_0_30px_-10px_hsl(340_85%_60%/0.5)]"
            }`}
          >
            <p className="text-[11px] font-heading font-semibold uppercase tracking-widest text-foreground/60">
              Decision Check Complete
            </p>
            <h2 className="font-heading font-bold text-2xl text-foreground">
              {score} / {totalQuestions} correct
            </h2>
            <p className="font-body text-sm text-foreground/80">
              {passed
                ? "Strong work — you applied the Book Insight and Company Case correctly. Quiz unlocked."
                : `You need ${minPass} of ${totalQuestions} correct to unlock the Quiz. Take another look at the Insight, then try again.`}
            </p>

            <div className="flex items-center justify-center gap-1.5 pt-1">
              {results.map((r, i) => (
                <span
                  key={i}
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-heading font-semibold ${
                    r
                      ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-300"
                      : "border-rose-400/50 bg-rose-400/15 text-rose-300"
                  }`}
                >
                  {i + 1}
                </span>
              ))}
            </div>
          </div>

          {lumiEnabled && (
            <LumiCoachingCard
              line={passed ? getLumiDecidePassCoaching(lessonId) : getLumiDecideMissCoaching(lessonId)}
              variant="inline"
              contextId={`${lessonId}:decide-results`}
            />
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {!passed && (
              <>
                <Button variant="outline" onClick={onReviewInsight} className="gap-2">
                  <ArrowLeft className="h-4 w-4" /> Review Insight
                </Button>
                <Button onClick={handleRetry} className="gap-2">
                  <RotateCcw className="h-4 w-4" /> Try Decision Check again
                </Button>
              </>
            )}
            {passed && (
              <Button onClick={onPass} className="gap-2 w-full sm:w-auto bg-gradient-to-r from-[hsl(225_75%_50%)] via-[hsl(280_75%_55%)] to-[hsl(330_80%_55%)] text-white shadow-[0_0_24px_-8px_hsl(310_90%_60%/0.7)]">
                Continue to Quiz <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DecideShell>
    );
  }

  // ───────── Active question ─────────
  const SourceIcon = sourceMeta?.icon ?? Sparkles;

  return (
    <DecideShell lessonName={lessonName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[11px] font-heading font-semibold uppercase tracking-widest text-foreground/60">
            <span>Question {currentIdx + 1} of {totalQuestions}</span>
            <span className="h-1 w-1 rounded-full bg-foreground/30" />
            <span style={{ color: sourceMeta?.tint }}>{sourceMeta?.label}</span>
          </div>
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground leading-tight">
            Applied Decision Check
          </h2>
          <p className="font-body text-sm text-foreground/70">
            Use the Book Insight and Company Case to answer these.
          </p>

          {/* Evidence reminder chips */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(265_90%_72%/0.35)] bg-[hsl(265_90%_72%/0.08)] px-2.5 py-1 text-[11px] font-heading font-medium text-[hsl(265_90%_82%)]">
              <BookOpen className="h-3 w-3" /> Book Insight
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(150_75%_60%/0.35)] bg-[hsl(150_75%_60%/0.08)] px-2.5 py-1 text-[11px] font-heading font-medium text-[hsl(150_75%_72%)]">
              <Building2 className="h-3 w-3" /> Company Case
            </span>
          </div>
        </div>

        {/* Question card */}
        <div
          className="relative rounded-2xl border bg-card/70 backdrop-blur-md p-5 md:p-6 space-y-5 shadow-[0_0_28px_-12px_hsl(190_80%_60%/0.35)]"
          style={{ borderColor: `${sourceMeta?.tint}55` }}
        >
          <div
            className="pointer-events-none absolute inset-x-6 top-0 h-px"
            style={{
              background: `linear-gradient(to right, transparent, ${sourceMeta?.tint}aa, transparent)`,
            }}
          />
          <div className="flex items-start gap-3">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${sourceMeta?.tint}22` }}
            >
              <SourceIcon className="h-4 w-4" style={{ color: sourceMeta?.tint }} />
            </div>
            <p className="font-body text-[15px] md:text-base leading-[1.7] text-foreground/90">
              {question.question_text}
            </p>
          </div>

          {/* Choices */}
          <div className="space-y-2.5">
            {question.answer_choices.map((choice, idx) => {
              const isSelected = selectedIdx === idx;
              const isCorrect = idx === question.correct_index;
              const showCorrect = submitted && isCorrect;
              const showIncorrect = submitted && isSelected && !isCorrect;
              const dim = submitted && !isSelected && !isCorrect;

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelect(idx)}
                  disabled={submitted}
                  className={`group flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition-all duration-200 ${
                    showCorrect
                      ? "border-emerald-400/60 bg-emerald-400/10 shadow-[0_0_20px_-6px_hsl(150_85%_60%/0.55)]"
                      : showIncorrect
                      ? "border-rose-400/60 bg-rose-400/10 shadow-[0_0_20px_-6px_hsl(340_85%_60%/0.55)]"
                      : isSelected
                      ? "border-primary/60 bg-primary/10 shadow-[0_0_18px_-6px_hsl(var(--primary)/0.5)]"
                      : dim
                      ? "border-border/40 bg-card/40 opacity-60"
                      : "border-border/60 bg-card/60 hover:border-primary/40 hover:bg-primary/5 hover:shadow-[0_0_16px_-8px_hsl(var(--primary)/0.45)]"
                  }`}
                >
                  <div
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                      showCorrect
                        ? "border-emerald-400 bg-emerald-400/20"
                        : showIncorrect
                        ? "border-rose-400 bg-rose-400/20"
                        : isSelected
                        ? "border-primary bg-primary/20"
                        : "border-border"
                    }`}
                  >
                    {showCorrect ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
                    ) : showIncorrect ? (
                      <XCircle className="h-3.5 w-3.5 text-rose-300" />
                    ) : isSelected ? (
                      <span className="h-2 w-2 rounded-full bg-primary" />
                    ) : null}
                  </div>
                  <p className="font-body text-sm leading-relaxed text-foreground/85">
                    {choice}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          {submitted && (
            <div
              className={`rounded-xl border p-4 space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300 ${
                selectedIdx === question.correct_index
                  ? "border-emerald-400/40 bg-emerald-400/5"
                  : "border-rose-400/40 bg-rose-400/5"
              }`}
            >
              <p className="text-[10px] font-heading font-semibold uppercase tracking-widest text-foreground/60">
                {selectedIdx === question.correct_index ? "Correct" : "Not quite"} ·{" "}
                {question.related_insight_section}
              </p>
              <p className="font-body text-sm leading-[1.7] text-foreground/90">
                {selectedIdx === question.correct_index
                  ? question.correct_explanation
                  : question.incorrect_explanations?.[String(selectedIdx)] ||
                    `The correct answer points back to the ${question.related_insight_section}. ${question.correct_explanation}`}
              </p>
              {selectedIdx !== question.correct_index && (
                <p className="font-body text-xs leading-[1.7] text-foreground/70 pt-1 border-t border-border/40">
                  <span className="font-semibold text-foreground/80">Correct answer: </span>
                  {question.answer_choices[question.correct_index]}
                </p>
              )}
            </div>
          )}

          {/* Lumi coaching for missed Decide questions (beta path only) */}
          {submitted && lumiEnabled && selectedIdx !== question.correct_index && (
            <LumiCoachingCard
              line={getLumiDecideMissCoaching(lessonId)}
              variant="inline"
              contextId={`${lessonId}:${question.question_id}`}
            />
          )}
        </div>

        {/* Action bar */}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {!submitted ? (
            <Button
              onClick={handleSubmit}
              disabled={selectedIdx === null}
              size="lg"
              className="gap-2 w-full sm:w-auto sm:min-w-[140px] px-5 font-heading font-semibold text-cyan-50 border border-cyan-300/50 bg-gradient-to-r from-[hsl(210_80%_28%/0.85)] via-[hsl(220_75%_32%/0.85)] to-[hsl(265_70%_36%/0.85)] backdrop-blur-xl shadow-[0_0_28px_-8px_hsl(190_95%_60%/0.7),inset_0_1px_0_0_hsl(190_100%_85%/0.35)] hover:from-[hsl(210_85%_34%/0.95)] hover:via-[hsl(220_80%_38%/0.95)] hover:to-[hsl(265_75%_42%/0.95)] hover:border-cyan-200/70 hover:shadow-[0_0_36px_-4px_hsl(190_100%_65%/0.85),inset_0_1px_0_0_hsl(190_100%_90%/0.45)] active:scale-[0.98] active:shadow-[0_0_18px_-6px_hsl(190_95%_60%/0.6)] focus-visible:ring-2 focus-visible:ring-cyan-300/70 focus-visible:ring-offset-0 transition-all disabled:opacity-40 disabled:shadow-none disabled:border-cyan-300/20 disabled:from-[hsl(220_30%_25%/0.6)] disabled:via-[hsl(220_30%_25%/0.6)] disabled:to-[hsl(220_30%_25%/0.6)]"
            >
              Submit <ArrowRight className="h-4 w-4" />
            </Button>
          ) : isLastQuestion ? (
            <Button
              onClick={() => { playSfx("result_reveal"); setResults((prev) => [...prev]); }}
              className="gap-2 w-full sm:w-auto bg-gradient-to-r from-[hsl(225_75%_50%)] via-[hsl(280_75%_55%)] to-[hsl(330_80%_55%)] text-white shadow-[0_0_24px_-8px_hsl(310_90%_60%/0.7)]"
              // results length already === totalQuestions after submit on last question,
              // so the component re-renders into the results screen automatically.
            >
              See results <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleNext} className="gap-2 w-full sm:w-auto">
              Next question <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </DecideShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Shell with background, header, evidence framing
// ─────────────────────────────────────────────────────────────

function DecideShell({
  lessonName,
  children,
}: {
  lessonName: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="relative min-h-screen bg-background bg-cover bg-center bg-no-repeat bg-fixed"
      style={{ backgroundImage: `url(${decideBg.url})` }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-background/85 via-background/70 to-background/92" />
      <div className="relative sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-primary/15">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center shadow-[0_0_14px_-4px_hsl(var(--primary)/0.7)]">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-muted-foreground font-body uppercase tracking-[0.18em]">
              Applied Decision Check
            </p>
            <h1 className="font-heading font-bold text-sm text-foreground truncate">
              {lessonName}
            </h1>
          </div>
        </div>
      </div>

      <main className="relative max-w-5xl mx-auto px-4 py-8 pb-24">{children}</main>
    </div>
  );
}